import type { ChatColleague, ChatConversation, ChatMessage } from "@/lib/chatApi";
import { memberLabel } from "@/lib/chatFormat";

export type MentionQuery = {
  start: number;
  query: string;
};

/** Membres d’un groupe (colleagues en repli si `members` incomplet). */
export function groupMentionMembers(
  selected: ChatConversation | null,
  colleagues: ChatColleague[],
  selfId: number,
  includeSelf = false
): ChatColleague[] {
  if (!selected || selected.type !== "group") return [];
  const map = new Map<number, ChatColleague>();
  for (const m of selected.members ?? []) {
    map.set(Number(m.id), m);
  }
  for (const rawId of selected.member_ids ?? []) {
    const id = Number(rawId);
    if (map.has(id)) continue;
    const c = colleagues.find((x) => Number(x.id) === id);
    if (c) map.set(id, c);
  }
  return [...map.values()].filter((m) => includeSelf || Number(m.id) !== selfId);
}

export function mentionLabel(m: ChatColleague): string {
  return memberLabel(m, m.username).replace(/\s+/g, " ").trim();
}

/** Libellés à surligner (`@Prénom` et `@username`). */
export function mentionHighlightLabels(
  selected: ChatConversation | null,
  colleagues: ChatColleague[],
  selfId: number
): string[] {
  const people = groupMentionMembers(selected, colleagues, selfId, true);
  const labels = new Set<string>();
  for (const p of people) {
    const dn = p.display_name?.replace(/\s+/g, " ").trim();
    if (dn) labels.add(dn);
    const un = p.username?.trim();
    if (un) labels.add(un);
  }
  return [...labels].sort((a, b) => b.length - a.length);
}

/** `@` en début de mot, requête jusqu’au curseur (espaces OK pour « Marie Du »). */
export function findMentionQuery(text: string, caret: number): MentionQuery | null {
  const pos = Math.max(0, Math.min(caret, text.length));
  const before = text.slice(0, pos);
  const at = before.lastIndexOf("@");
  if (at < 0) return null;
  if (at > 0 && !/[\s\u00A0([{'"]/.test(before.charAt(at - 1))) return null;
  const query = before.slice(at + 1);
  if (query.includes("\n") || /^\s/.test(query) || /\s$/.test(query) || /\s{2}/.test(query)) {
    return null;
  }
  return { start: at, query };
}

export function filterMentionCandidates(
  members: ChatColleague[],
  query: string
): ChatColleague[] {
  const q = query.replace(/\s+/g, " ").trim().toLowerCase();
  const scored = members
    .map((m) => {
      const label = mentionLabel(m).toLowerCase();
      const username = (m.username || "").toLowerCase();
      if (!q) return { m, score: 1 };
      if (label.startsWith(q) || username.startsWith(q)) return { m, score: 3 };
      if (label.includes(q) || username.includes(q)) return { m, score: 2 };
      return { m, score: 0 };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || mentionLabel(a.m).localeCompare(mentionLabel(b.m), "fr"));
  return scored.map((x) => x.m);
}

export function insertMention(
  text: string,
  caret: number,
  label: string
): { text: string; caret: number } {
  const q = findMentionQuery(text, caret);
  const insert = `@${label} `;
  if (!q) {
    const next = `${text.slice(0, caret)}${insert}${text.slice(caret)}`;
    return { text: next, caret: caret + insert.length };
  }
  const next = `${text.slice(0, q.start)}${insert}${text.slice(caret)}`;
  return { text: next, caret: q.start + insert.length };
}

export function formatCopyText(m: ChatMessage): string {
  const body = (m.body || "").trim();
  const extra = (m.attachments ?? [])
    .map((a) => (a.url || a.name || "").trim())
    .filter((u) => u && !body.includes(u));
  if (body && extra.length > 0) return `${body}\n${extra.join("\n")}`;
  if (body) return body;
  return extra.join("\n");
}

export function formatForwardBody(m: ChatMessage, author: string): string {
  const who = author.trim() || "quelqu’un";
  const parts = [`Transféré de ${who}`];
  const body = (m.body || "").trim();
  if (body) {
    parts.push("", body);
  }
  for (const a of m.attachments ?? []) {
    const url = (a.url || "").trim();
    if (url && !body.includes(url)) parts.push(url);
  }
  return parts.join("\n");
}

export type MentionRange = { start: number; end: number };

export function findMentionRanges(source: string, labels: string[]): MentionRange[] {
  const ordered = [...labels].filter(Boolean).sort((a, b) => b.length - a.length);
  const ranges: MentionRange[] = [];
  const lower = source.toLowerCase();
  for (const label of ordered) {
    const needle = `@${label}`.toLowerCase();
    let from = 0;
    while (from < source.length) {
      const i = lower.indexOf(needle, from);
      if (i < 0) break;
      const beforeOk = i === 0 || /[\s\u00A0([{'"]/.test(source.charAt(i - 1));
      const after = source.charAt(i + needle.length);
      const afterOk = after === "" || /[\s\u00A0,.:;!?)]'"\n]/.test(after);
      const overlap = ranges.some((r) => i < r.end && i + needle.length > r.start);
      if (beforeOk && afterOk && !overlap) {
        ranges.push({ start: i, end: i + needle.length });
      }
      from = i + 1;
    }
  }
  ranges.sort((a, b) => a.start - b.start);
  return ranges;
}
